import { Minus, Plus, ReceiptText, Trash2, TriangleAlert, X } from "lucide-react";
import { useMemo } from "react";

import { ProductThumb } from "@/components/ProductThumb.tsx";
import { computeTotals, useCartStore } from "@/stores/cart.ts";
import { formatPEN } from "@/lib/utils.ts";
import { useStock } from "@/queries/inventory.ts";
import { useTerminalStore } from "@/stores/terminal.ts";

interface Props {
  onCheckout: () => void;
  /** Quién atiende, para el encabezado del ticket. */
  cashierName?: string;
}

/** Ticket en curso: miniaturas, cantidad en pastilla y total destacado. */
export function Cart({ onCheckout, cashierName }: Props) {
  const lines = useCartStore((s) => s.lines);
  const setQuantity = useCartStore((s) => s.setQuantity);
  const removeProduct = useCartStore((s) => s.removeProduct);
  const clear = useCartStore((s) => s.clear);

  const totals = computeTotals(lines);

  // Stock de la sucursal de la caja: solo para avisar, la venta no se bloquea.
  const branchId = useTerminalStore((s) => s.terminal?.branchId);
  const stock = useStock({ branchId });
  const stockById = useMemo(
    () => new Map((stock.data ?? []).map((s) => [s.product_id, Number(s.stock)])),
    [stock.data],
  );
  const empty = lines.length === 0;

  return (
    <div className="flex h-full flex-col bg-white">
      <div className="flex items-center justify-between px-5 pt-5 pb-3">
        <div>
          <h2 className="text-lg font-extrabold text-slate-900">Ticket</h2>
          <p className="text-xs font-medium text-slate-500">
            {empty
              ? "Escanea o toca un producto"
              : `${totals.itemCount} ítem${totals.itemCount > 1 ? "s" : ""}`}
            {cashierName ? ` · ${cashierName}` : ""}
          </p>
        </div>
        {!empty && (
          <button
            type="button"
            onClick={clear}
            className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-semibold text-slate-500 hover:bg-slate-100 hover:text-slate-800"
          >
            <Trash2 className="h-3.5 w-3.5" /> Vaciar
          </button>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-auto px-4">
        {empty ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center text-slate-400">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-50">
              <ReceiptText className="h-7 w-7" />
            </div>
            <p className="text-sm">El ticket está vacío.</p>
          </div>
        ) : (
          <ul className="space-y-2.5 pb-2">
            {lines.map((line) => {
              const lineTotal = Number(line.product.base_price) * line.quantity;
              const available = stockById.get(line.productId);
              const short =
                line.product.track_inventory &&
                available !== undefined &&
                line.quantity > available;
              return (
                <li key={line.productId} className="flex gap-3 rounded-2xl bg-[#f8fafa] p-2 pr-2.5">
                  <ProductThumb
                    imagePath={line.product.image_path}
                    seed={line.productId}
                    size="thumb"
                    className="h-12 w-12 shrink-0 rounded-xl"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start gap-1">
                      <p className="min-w-0 flex-1 truncate text-sm font-bold text-slate-900">
                        {line.product.name}
                      </p>
                      <button
                        type="button"
                        onClick={() => removeProduct(line.productId)}
                        aria-label={`Quitar ${line.product.name}`}
                        className="rounded-md p-0.5 text-slate-400 hover:bg-white hover:text-red-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="mt-1 flex items-center justify-between gap-2">
                      <div className="min-w-0 leading-tight">
                        <p className="text-sm font-extrabold tabular-nums text-slate-900">
                          {formatPEN(lineTotal)}
                        </p>
                        <p className="text-[11px] text-slate-500">
                          {formatPEN(line.product.base_price)} c/u
                        </p>
                      </div>
                      <div className="flex items-center rounded-xl border border-slate-200 bg-white p-0.5">
                        <button
                          type="button"
                          onClick={() => setQuantity(line.productId, line.quantity - 1)}
                          aria-label={`Quitar uno de ${line.product.name}`}
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100"
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                        <span className="min-w-[1.75rem] text-center text-sm font-extrabold tabular-nums">
                          {line.quantity}
                        </span>
                        <button
                          type="button"
                          onClick={() => setQuantity(line.productId, line.quantity + 1)}
                          aria-label={`Agregar uno de ${line.product.name}`}
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    {short && (
                      <p className="mt-1 flex items-center gap-1 text-xs font-semibold text-amber-700">
                        <TriangleAlert className="h-3.5 w-3.5" />
                        Stock disponible: {Math.max(available ?? 0, 0)}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="m-4 flex flex-col gap-1.5 rounded-[1.25rem] bg-slate-900 p-5 text-white">
        <div className="flex justify-between text-sm text-slate-300">
          <span>Subtotal</span>
          <span className="tabular-nums">{formatPEN(totals.subtotal)}</span>
        </div>
        <div className="flex justify-between text-sm text-slate-300">
          <span>IGV</span>
          <span className="tabular-nums">{formatPEN(totals.igvAmount)}</span>
        </div>
        <div className="mt-1 flex items-baseline justify-between">
          <span className="font-bold">Total</span>
          <span className="text-3xl font-extrabold tabular-nums">{formatPEN(totals.total)}</span>
        </div>
        <button
          type="button"
          onClick={onCheckout}
          disabled={empty}
          className="mt-3 w-full rounded-2xl bg-brand-600 py-4 text-lg font-extrabold text-white hover:bg-brand-500 active:bg-brand-700 disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-400"
        >
          Cobrar {formatPEN(totals.total)}
        </button>
      </div>
    </div>
  );
}
